/**
 * Offline write queue for the Pit Engineer module.
 *
 * Pattern:
 *  - When a write call (PATCH event tanda, PATCH tire, POST tire) succeeds,
 *    nothing is persisted in localStorage.
 *  - When the call fails AND the browser is offline, the request is queued
 *    in localStorage and the UI keeps the optimistic state.
 *  - When `online` fires, we attempt to flush each item in order. Items that
 *    keep failing for non-network reasons (4xx) are dropped after notifying.
 *
 * The queue is keyed by a monotonic id and stores everything needed to
 * replay the request later without depending on React state.
 */
import axios from 'axios';
import { API } from '../config/api';

const STORAGE_KEY = 'datapro_pit_offline_queue_v1';
const listeners = new Set();

const read = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const write = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  listeners.forEach((fn) => fn());
};

export const getQueue = () => read();
export const getPendingCount = () => read().length;

export const enqueue = (item) => {
  const items = read();
  items.push({
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    queued_at: new Date().toISOString(),
    ...item,
  });
  write(items);
};

const dropById = (id) => {
  write(read().filter((x) => x.id !== id));
};

let flushing = false;
export const flushQueue = async () => {
  if (flushing) return { sent: 0, failed: 0 };
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    const items = read();
    for (const item of items) {
      try {
        await axios.request({
          method: item.method,
          url: `${API}${item.path}`,
          data: item.data,
          withCredentials: true,
        });
        dropById(item.id);
        sent += 1;
      } catch (err) {
        const status = err.response?.status;
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Non-recoverable client error — drop so we don't loop forever.
          dropById(item.id);
          failed += 1;
        } else {
          // Network or server error — leave it queued for the next flush.
          break;
        }
      }
    }
  } finally {
    flushing = false;
  }
  return { sent, failed };
};

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Auto-flush whenever the browser regains connectivity.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushQueue();
  });
  // Also try a flush on load in case the tab was reopened with pending items.
  window.addEventListener('load', () => {
    if (navigator.onLine) flushQueue();
  });
}
